import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import accountsApi from '../../services/accountsApi';
import { useAuth } from '../../context/AuthContext';
import { ArrowLeft, Send, X, Check, ThumbsDown, FileCheck, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';

const FINANCE_ROLES = ['Super Admin', 'Secretary', 'Director'];

const STATUS_COLORS = {
  Draft: 'bg-gray-100 text-gray-600',
  Submitted: 'bg-yellow-100 text-yellow-700',
  Approved: 'bg-blue-100 text-blue-700',
  Posted: 'bg-green-100 text-green-700',
  Rejected: 'bg-red-100 text-red-700',
  Cancelled: 'bg-gray-100 text-gray-500',
  Reversed: 'bg-purple-100 text-purple-700',
};

const ReasonModal = ({ title, onConfirm, onCancel, required }) => {
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="bg-blue-900 text-white p-4 rounded-t-xl flex justify-between items-center">
          <h2 className="text-lg font-bold">{title}</h2>
          <button onClick={onCancel} className="text-white text-2xl font-bold">×</button>
        </div>
        <div className="p-6">
          <label className="block text-xs font-medium text-gray-700 mb-1">Reason {required && '(required)'}</label>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          <div className="flex gap-3 mt-4">
            <button onClick={onCancel} className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
            <button
              onClick={() => { if (required && !reason.trim()) { toast.error('Reason is required'); return; } onConfirm(reason); }}
              className="flex-1 bg-blue-800 text-white py-2 rounded-lg text-sm hover:bg-blue-900">Confirm</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const VoucherDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [voucher, setVoucher] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null); // 'reject' | 'cancel' | 'reverse' | null

  const isFinance = FINANCE_ROLES.includes(user?.role);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [voucherRes, auditRes] = await Promise.all([
        accountsApi.get(`/vouchers/${id}`),
        accountsApi.get(`/vouchers/${id}/audit-log`),
      ]);
      setVoucher(voucherRes.data.voucher);
      setAuditLog(auditRes.data.audit_log);
    } catch (err) {
      toast.error('Failed to load voucher');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const runAction = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(successMsg);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Action failed');
    } finally {
      setBusy(false);
      setModal(null);
    }
  };

  const handleSubmit = () => runAction(() => accountsApi.put(`/vouchers/${id}/submit`), 'Voucher submitted');
  const handleApprove = () => runAction(() => accountsApi.put(`/vouchers/${id}/approve`), 'Voucher approved');
  const handlePost = () => runAction(() => accountsApi.put(`/vouchers/${id}/post`), 'Voucher posted');
  const handleReject = (reason) => runAction(() => accountsApi.put(`/vouchers/${id}/reject`, { rejection_reason: reason }), 'Voucher rejected');
  const handleCancel = (reason) => runAction(() => accountsApi.put(`/vouchers/${id}/cancel`, { reason }), 'Voucher cancelled');
  const handleReverse = (reason) => runAction(() => accountsApi.post(`/vouchers/${id}/reverse`, { reason }), 'Voucher reversed');

  if (loading) return <Layout title="Voucher"><div className="text-center py-8 text-gray-500">Loading...</div></Layout>;
  if (!voucher) return <Layout title="Voucher"><div className="text-center py-8 text-gray-500">Voucher not found</div></Layout>;

  const debitTotal = voucher.lines.reduce((s, l) => s + Number(l.debit || 0), 0);
  const creditTotal = voucher.lines.reduce((s, l) => s + Number(l.credit || 0), 0);

  return (
    <Layout title={`Voucher ${voucher.voucher_no}`}>
      <button onClick={() => navigate('/vouchers')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
        <ArrowLeft size={14} /> Back to vouchers
      </button>

      <div className="bg-white rounded-xl shadow p-6 mb-4">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-gray-800">{voucher.voucher_no}</h2>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[voucher.status]}`}>{voucher.status}</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">{voucher.purpose}</p>
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            {voucher.status === 'Draft' && (
              <>
                <button onClick={handleSubmit} disabled={busy} className="flex items-center gap-1 bg-blue-800 text-white px-3 py-2 rounded-lg text-xs hover:bg-blue-900 disabled:opacity-50">
                  <Send size={14} /> Submit
                </button>
                <button onClick={() => setModal('cancel')} disabled={busy} className="flex items-center gap-1 border border-gray-300 text-gray-700 px-3 py-2 rounded-lg text-xs hover:bg-gray-50">
                  <X size={14} /> Cancel
                </button>
              </>
            )}
            {voucher.status === 'Submitted' && (
              <>
                {isFinance && (
                  <>
                    <button onClick={handleApprove} disabled={busy} className="flex items-center gap-1 bg-green-700 text-white px-3 py-2 rounded-lg text-xs hover:bg-green-800 disabled:opacity-50">
                      <Check size={14} /> Approve
                    </button>
                    <button onClick={() => setModal('reject')} disabled={busy} className="flex items-center gap-1 bg-red-600 text-white px-3 py-2 rounded-lg text-xs hover:bg-red-700 disabled:opacity-50">
                      <ThumbsDown size={14} /> Reject
                    </button>
                  </>
                )}
                <button onClick={() => setModal('cancel')} disabled={busy} className="flex items-center gap-1 border border-gray-300 text-gray-700 px-3 py-2 rounded-lg text-xs hover:bg-gray-50">
                  <X size={14} /> Cancel
                </button>
              </>
            )}
            {voucher.status === 'Approved' && isFinance && (
              <>
                <button onClick={handlePost} disabled={busy} className="flex items-center gap-1 bg-blue-800 text-white px-3 py-2 rounded-lg text-xs hover:bg-blue-900 disabled:opacity-50">
                  <FileCheck size={14} /> Post
                </button>
                <button onClick={() => setModal('cancel')} disabled={busy} className="flex items-center gap-1 border border-gray-300 text-gray-700 px-3 py-2 rounded-lg text-xs hover:bg-gray-50">
                  <X size={14} /> Cancel
                </button>
              </>
            )}
            {voucher.status === 'Posted' && isFinance && (
              <button onClick={() => setModal('reverse')} disabled={busy} className="flex items-center gap-1 bg-purple-700 text-white px-3 py-2 rounded-lg text-xs hover:bg-purple-800 disabled:opacity-50">
                <RotateCcw size={14} /> Reverse
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4 text-sm border-t border-gray-100 pt-4">
          <div><div className="text-gray-500 text-xs">Cost Center</div><div className="font-medium text-gray-800">{voucher.cost_center_code ? `${voucher.cost_center_code} — ${voucher.cost_center_name}` : '—'}</div></div>
          <div><div className="text-gray-500 text-xs">Fund</div><div className="font-medium text-gray-800">{voucher.fund_code ? `${voucher.fund_code} — ${voucher.fund_name}` : '—'}</div></div>
          <div><div className="text-gray-500 text-xs">Institution</div><div className="font-medium text-gray-800">{voucher.institution_code ? `${voucher.institution_code} — ${voucher.institution_name}` : '—'}</div></div>
          <div><div className="text-gray-500 text-xs">Installment</div><div className="font-medium text-gray-800">{voucher.installment_number ? `#${voucher.installment_number} (${voucher.installment_status})` : '—'}</div></div>
          <div><div className="text-gray-500 text-xs">Vehicle</div><div className="font-medium text-gray-800">{voucher.vehicle_code ? `${voucher.vehicle_code} — ${voucher.vehicle_registration_number || ''}` : '—'}</div></div>
          <div><div className="text-gray-500 text-xs">Vendor</div><div className="font-medium text-gray-800">{voucher.vendor_code || '—'}</div></div>
          <div><div className="text-gray-500 text-xs">Vendor Bill</div><div className="font-medium text-gray-800">{voucher.vendor_bill_no ? `#${voucher.vendor_bill_no}` : '—'}</div></div>
          <div><div className="text-gray-500 text-xs">Payment Mode</div><div className="font-medium text-gray-800">{voucher.payment_mode || '—'}</div></div>
          <div><div className="text-gray-500 text-xs">Accounting Date</div><div className="font-medium text-gray-800">{voucher.accounting_date?.slice(0, 10) || '—'}</div></div>
        </div>
        {voucher.rejection_reason && (
          <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
            <strong>Rejection reason:</strong> {voucher.rejection_reason}
          </div>
        )}
        {voucher.journal_entry_id && (
          <div className="mt-3 text-sm">
            <Link to={`/journal-entries/${voucher.journal_entry_id}`} className="text-blue-700 hover:underline">
              View posted journal entry →
            </Link>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden mb-4">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-700">Lines</h3>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left px-4 py-2 text-gray-500 font-medium text-xs">Account</th>
              <th className="text-left px-4 py-2 text-gray-500 font-medium text-xs">Description</th>
              <th className="text-right px-4 py-2 text-gray-500 font-medium text-xs">Debit</th>
              <th className="text-right px-4 py-2 text-gray-500 font-medium text-xs">Credit</th>
            </tr>
          </thead>
          <tbody>
            {voucher.lines.map(l => (
              <tr key={l.id} className="border-t border-gray-50">
                <td className="px-4 py-2 text-gray-700">{l.account_code} — {l.account_name}</td>
                <td className="px-4 py-2 text-gray-500">{l.description || '—'}</td>
                <td className="px-4 py-2 text-right text-gray-800">{Number(l.debit || 0) > 0 ? Number(l.debit).toFixed(2) : ''}</td>
                <td className="px-4 py-2 text-right text-gray-800">{Number(l.credit || 0) > 0 ? Number(l.credit).toFixed(2) : ''}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-gray-200 font-semibold">
              <td colSpan={2} className="px-4 py-2 text-right text-gray-600 text-xs">Total</td>
              <td className="px-4 py-2 text-right text-gray-800">{debitTotal.toFixed(2)}</td>
              <td className="px-4 py-2 text-right text-gray-800">{creditTotal.toFixed(2)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-700">Audit Trail</h3>
        </div>
        <div className="divide-y divide-gray-50">
          {auditLog.map(entry => (
            <div key={entry.id} className="px-4 py-3 text-sm flex items-center justify-between">
              <div>
                <span className="font-medium text-gray-800">{entry.action}</span>
                {entry.previous_status && <span className="text-gray-400"> ({entry.previous_status} → {entry.new_status})</span>}
                {entry.reason && <div className="text-xs text-gray-500 mt-0.5">{entry.reason}</div>}
              </div>
              <span className="text-xs text-gray-400">{entry.created_at?.slice(0, 19).replace('T', ' ')}</span>
            </div>
          ))}
        </div>
      </div>

      {modal === 'reject' && (
        <ReasonModal title="Reject Voucher" required onCancel={() => setModal(null)} onConfirm={handleReject} />
      )}
      {modal === 'cancel' && (
        <ReasonModal title="Cancel Voucher" onCancel={() => setModal(null)} onConfirm={handleCancel} />
      )}
      {modal === 'reverse' && (
        <ReasonModal title="Reverse Voucher" required onCancel={() => setModal(null)} onConfirm={handleReverse} />
      )}
    </Layout>
  );
};

export default VoucherDetail;
